import { groq } from "hydrogen-sanity/groq";
import { HEADERS } from "./fragments/headers";
import { SECTIONS } from "./fragments/sections";
import { BASE_LINK, CONTENT, IMAGE, LINK } from "./fragments/components";

// Query 3 levels deep of breadcrumb parent pages
export const queryBreadcrumbs = groq`
   parent -> {
        slug,
        _type,
        title,
        parent -> {
            slug,
            _type,
            title,
            parent -> {
                slug,
                _type,
                title,
            }
        }
    }
`;

export const queryPage = groq`
    *[_type == "page" && slug.current == $slug][0] {
        ...,
        _originalId,
        seo {
            ...,
            image {
                ...,
                asset -> {
                    ...
                }
            }
        },
        template-> {
             ...,
            _type,
            ${LINK},
            ${HEADERS}
            ${SECTIONS}
        },
        ${queryBreadcrumbs}

    }
`;

export const queryPartnersAll = groq`
  *[_type == "partner"] | order(orderRank) {
    _id,
    _type,
    "title": name,
    "shortText": shortDescription,
    name,
    shortDescription,
    slug,
    partnerType,
    orderRank,
    link,
    ${IMAGE},
  }
`;

export const queryMember = groq`
    *[_type == "member" && slug.current == $slug][0] {
        ...,
        ${IMAGE},
        ${LINK},
        teams[]-> {
            ...
        }
    }
`;

// @link https://hdoro.dev/minimal-sanity-io-pagination
const ITEMS_PER_PAGE_MEMBERS = 9;
const SLICE_PAGINATION_STATIC_MEMBERS = groq`[(0 * ${ITEMS_PER_PAGE_MEMBERS})...(0 + 1) * ${ITEMS_PER_PAGE_MEMBERS}]`;
const SLICE_PAGINATION_DYNAMIC_MEMBERS = groq`[($pageIndex * ${ITEMS_PER_PAGE_MEMBERS})...($pageIndex + 1) * ${ITEMS_PER_PAGE_MEMBERS}]`;
// const FILTER_MEMBERS = groq`*[_type == "member"]`;
const FILTER_MEMBERS = groq`*[_type == "member" && ($slug == null || $slug in teams[]->slug.current)] | order(orderRank)`;
const PROJECTION_PAGINATION_MEMBERS = groq`
    {
        "totalPageCount": count(${FILTER_MEMBERS}._id) / ${ITEMS_PER_PAGE_MEMBERS},
        "pageNumber": $pageIndex + 1,
    }
`;

export const queryMembersStatic = groq`
    {
        "items":
            ${FILTER_MEMBERS}
            // SLICE_PAGINATION_STATIC_MEMBERS used instead of SLICE_PAGINATION_DYNAMIC_MEMBERS
            // Needed to avoid following TypeGen error 
            // Error generating types for query "queryMembers" in "./app/queries/page.ts": slicing must use constant numbers
            ${SLICE_PAGINATION_STATIC_MEMBERS}
            {
                ${IMAGE},
                firstname,
                lastname,
                role,
                slug,
                _type,
text,
shortText,
                
            },
        "pagination":
            ${PROJECTION_PAGINATION_MEMBERS}
    }
`;

// Use QueryMembersStaticResult type instead of QueryMembersDynamicResult type
// Use queryMembersDynamic query instead of queryMembersStatic query
export const queryMembersDynamic = groq`
    {
        "items":
            ${FILTER_MEMBERS}
            ${SLICE_PAGINATION_DYNAMIC_MEMBERS}
            {
                ${IMAGE},
                firstname,
                lastname,
                role,
                slug,
                _type,
text,
shortText,
                },
        "pagination":
            ${PROJECTION_PAGINATION_MEMBERS}
    }
`;

const FILTER_RESEARCH_PAPERS = groq`*[_type == "researchPaper" && ($year == null || $year == year->title) && ($region == null || $region == region->slug.current)] | order(orderRank)`;
const ITEMS_PER_PAGE_RESEARCH_PAPERS = 6;
const SLICE_PAGINATION_STATIC_RESEARCH_PAPERS = groq`[(0 * ${ITEMS_PER_PAGE_RESEARCH_PAPERS})...(0 + 1) * ${ITEMS_PER_PAGE_RESEARCH_PAPERS}]`;
const SLICE_PAGINATION_DYNAMIC_RESEARCH_PAPERS = groq`[($pageIndex * ${ITEMS_PER_PAGE_RESEARCH_PAPERS})...($pageIndex + 1) * ${ITEMS_PER_PAGE_RESEARCH_PAPERS}]`;
const PROJECTION_PAGINATION_RESEARCH_PAPERS = groq`
    {
        "totalPageCount": count(${FILTER_RESEARCH_PAPERS}._id) / ${ITEMS_PER_PAGE_RESEARCH_PAPERS},
        "pageNumber": $pageIndex + 1,
    }
`;

export const queryResearchStatic = groq`
    {
        "items":
            ${FILTER_RESEARCH_PAPERS}
            ${SLICE_PAGINATION_STATIC_RESEARCH_PAPERS}
            {
                ...,
                ${IMAGE},
                year -> {
                    ...
                },
                region -> {
                    ...
                },
                document {
                    ...,
                    asset-> {...}
                }
            },
        "pagination":
            ${PROJECTION_PAGINATION_RESEARCH_PAPERS},
        // "filters": [
        //     {
        //         "title": "Year",
        //         "slug": "year",
        //         "items":
        //             *[_type == 'researchPaperYear'] | order(title desc) {
        //                 "title": string(title),
        //                 "slug": slug.current,
        //             }

        //     },
        //     {
        //         "title": "Region",
        //         "slug": "region",
        //         "items":
        //             *[_type == 'region'] {
        //                 "title": abbreviation,
        //                 "slug": slug.current,
        //             }

        //     },
        // ]
    }
`;

export const queryResearchDynamic = groq`
    {
        "items":
            ${FILTER_RESEARCH_PAPERS}
            ${SLICE_PAGINATION_DYNAMIC_RESEARCH_PAPERS}
            {
                ...,
                ${IMAGE},
                year -> {
                    ...
                },
                region -> {
                    ...
                },
                document {
                    ...,
                    asset-> {...}
                }
            },
        "pagination":
            ${PROJECTION_PAGINATION_RESEARCH_PAPERS},
        // "filters": [
        //     {
        //         "title": "Year",
        //         "slug": "year",
        //         "items":
        //             *[_type == 'researchPaperYear'] | order(title desc) {
        //                 "title": string(title),
        //                 "slug": slug.current,
        //             }

        //     },
        //     {
        //         "title": "Region",
        //         "slug": "region",
        //         "items":
        //             *[_type == 'region'] {
        //                 "title": abbreviation,
        //                 "slug": slug.current,
        //             }

        //     },
        // ]
    }
`;

export const queryFeaturedResearchStatic = groq`
    {
        "items":
            *[_type == "researchPaper" && featured == true] | order(orderRank)
            {
                ...,
                document {
                    asset-> {...}
                },
            }
    }
`;

export const queryFeaturedResearchDynamic = groq`
    {
        "items":
            *[_type == "researchPaper" && featured == true] | order(orderRank)
            {
                ...,
                document {
                    asset-> {...}
                },

            }
    }
`;
